import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { BACKEND_URL, API } from "@/lib/api";
import {
  Plus,
  Trash2,
  Users,
  Circle,
  Square,
  RectangleHorizontal,
  Move,
  UserPlus,
  UserMinus,
  ChevronRight,
  Check,
  X,
  Grip,
  Save,
  RotateCcw,
  ZoomIn,
  ZoomOut,
  Maximize2
} from "lucide-react";


// Table shape components
const TableShape = ({ table, isSelected, onClick, guests, onDragStart, onDrag, onDragEnd, scale }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e) => {
    if (e.button !== 0) return; // Only left click
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setStartPos({ x: e.clientX, y: e.clientY });
    onDragStart?.(table.id);
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const dx = (e.clientX - startPos.x) / scale;
    const dy = (e.clientY - startPos.y) / scale;
    onDrag?.(table.id, dx, dy);
    setStartPos({ x: e.clientX, y: e.clientY });
  }, [isDragging, startPos, scale, table.id, onDrag]);

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      onDragEnd?.(table.id);
    }
  }, [isDragging, table.id, onDragEnd]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const baseStyle = {
    position: 'absolute',
    left: table.position?.x || 0,
    top: table.position?.y || 0,
    cursor: isDragging ? 'grabbing' : 'grab',
    transition: isDragging ? 'none' : 'box-shadow 0.2s',
    zIndex: isDragging ? 100 : (isSelected ? 50 : 1)
  };

  const occupancy = guests.length;
  const isFull = occupancy >= table.capacity;
  const occupancyColor = isFull ? 'bg-red-100 text-red-700' : occupancy > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600';

  const renderShape = () => {
    const sharedProps = {
      onClick: (e) => { e.stopPropagation(); onClick?.(table); },
      onMouseDown: handleMouseDown,
      className: `
        flex flex-col items-center justify-center text-center p-2
        border-2 shadow-md hover:shadow-lg
        ${isSelected ? 'border-primary ring-2 ring-primary/30' : 'border-gray-300'}
        ${isDragging ? 'shadow-xl' : ''}
      `,
      style: {
        ...baseStyle,
        backgroundColor: table.color || '#f3f4f6'
      }
    };

    if (table.shape === 'round') {
      return (
        <div {...sharedProps} style={{ ...sharedProps.style, width: 100, height: 100, borderRadius: '50%' }}>
          <span className="font-medium text-xs truncate max-w-[80px]">{table.name}</span>
          <Badge variant="secondary" className={`text-[10px] mt-1 ${occupancyColor}`}>
            {occupancy}/{table.capacity}
          </Badge>
        </div>
      );
    }

    if (table.shape === 'square') {
      return (
        <div {...sharedProps} style={{ ...sharedProps.style, width: 100, height: 100, borderRadius: 8 }}>
          <span className="font-medium text-xs truncate max-w-[80px]">{table.name}</span>
          <Badge variant="secondary" className={`text-[10px] mt-1 ${occupancyColor}`}>
            {occupancy}/{table.capacity}
          </Badge>
        </div>
      );
    }

    // Rectangle (default)
    return (
      <div {...sharedProps} style={{ ...sharedProps.style, width: table.width || 140, height: table.height || 70, borderRadius: 8 }}>
        <span className="font-medium text-xs truncate max-w-[120px]">{table.name}</span>
        <Badge variant="secondary" className={`text-[10px] mt-1 ${occupancyColor}`}>
          {occupancy}/{table.capacity}
        </Badge>
      </div>
    );
  };

  return renderShape();
};

export default function SeatingPlan({ eventId, isOwner = true }) {
  const [tables, setTables] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [guests, setGuests] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddTable, setShowAddTable] = useState(false);
  const [showAssignGuest, setShowAssignGuest] = useState(false);
  const [scale, setScale] = useState(1);
  const [newTable, setNewTable] = useState({
    name: "",
    capacity: 8,
    shape: "round",
    color: "#f3f4f6"
  });

  const canvasRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [tablesRes, assignmentsRes, guestsRes, statsRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}/seating-plan/tables`, { withCredentials: true }),
        axios.get(`${API}/events/${eventId}/seating-plan/assignments`, { withCredentials: true }),
        axios.get(`${API}/events/${eventId}/guests`, { withCredentials: true }),
        axios.get(`${API}/events/${eventId}/seating-plan/stats`, { withCredentials: true })
      ]);

      setTables(tablesRes.data);
      setAssignments(assignmentsRes.data);
      setGuests(guestsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching seating data:", error);
      toast.error("Fehler beim Laden des Sitzplans");
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (eventId) {
      fetchData();
    }
  }, [eventId, fetchData]);

  const handleAddTable = async () => {
    if (!newTable.name.trim()) {
      toast.error("Bitte Tischnamen eingeben");
      return;
    }

    try {
      // Calculate initial position (spread tables out)
      const existingCount = tables.length;
      const col = existingCount % 4;
      const row = Math.floor(existingCount / 4);

      const tableData = {
        ...newTable,
        position: {
          x: 50 + col * 150,
          y: 50 + row * 130
        }
      };

      const response = await axios.post(
        `${API}/events/${eventId}/seating-plan/tables`,
        tableData,
        { withCredentials: true }
      );

      setTables([...tables, response.data]);
      setShowAddTable(false);
      setNewTable({ name: "", capacity: 8, shape: "round", color: "#f3f4f6" });
      toast.success("Tisch hinzugefügt!");
      fetchData(); // Refresh stats
    } catch (error) {
      toast.error("Fehler beim Hinzufügen des Tisches");
    }
  };

  const handleDeleteTable = async (tableId) => {
    try {
      await axios.delete(
        `${API}/events/${eventId}/seating-plan/tables/${tableId}`,
        { withCredentials: true }
      );

      setTables(tables.filter(t => t.id !== tableId));
      setAssignments(assignments.filter(a => a.table_id !== tableId));
      setSelectedTable(null);
      toast.success("Tisch gelöscht!");
      fetchData(); // Refresh stats
    } catch (error) {
      toast.error("Fehler beim Löschen des Tisches");
    }
  };

  const handleDrag = async (tableId, dx, dy) => {
    setTables(prev => prev.map(t => {
      if (t.id === tableId) {
        return {
          ...t,
          position: {
            x: Math.max(0, (t.position?.x || 0) + dx),
            y: Math.max(0, (t.position?.y || 0) + dy)
          }
        };
      }
      return t;
    }));
  };

  const handleDragEnd = async (tableId) => {
    const table = tables.find(t => t.id === tableId);
    if (!table) return;

    try {
      await axios.put(
        `${API}/events/${eventId}/seating-plan/tables/${tableId}`,
        { position: table.position },
        { withCredentials: true }
      );
    } catch (error) {
      console.error("Error saving position:", error);
    }
  };

  const handleAssignGuest = async (guestId) => {
    if (!selectedTable) return;

    try {
      await axios.post(
        `${API}/events/${eventId}/seating-plan/assignments`,
        { table_id: selectedTable.id, guest_id: guestId },
        { withCredentials: true }
      );

      toast.success("Gast zugewiesen!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Zuweisen");
    }
  };

  const handleRemoveAssignment = async (guestId) => {
    try {
      await axios.delete(
        `${API}/events/${eventId}/seating-plan/assignments/${guestId}`,
        { withCredentials: true }
      );

      toast.success("Zuweisung entfernt!");
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Entfernen der Zuweisung");
    }
  };

  const getGuestsAtTable = (tableId) => {
    const guestIds = assignments.filter(a => a.table_id === tableId).map(a => a.guest_id);
    return guests.filter(g => guestIds.includes(g.id));
  };

  const getUnassignedGuests = () => {
    const assignedIds = assignments.map(a => a.guest_id);
    return guests.filter(g => !assignedIds.includes(g.id));
  };

  const handleUpdateTable = async (tableId, updates) => {
    try {
      await axios.put(
        `${API}/events/${eventId}/seating-plan/tables/${tableId}`,
        updates,
        { withCredentials: true }
      );

      setTables(prev => prev.map(t => t.id === tableId ? { ...t, ...updates } : t));
      toast.success("Tisch aktualisiert!");
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with stats */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" />
          <h3 className="font-semibold text-lg">Sitzplan</h3>
        </div>

        {stats && (
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="flex items-center gap-1">
              <RectangleHorizontal className="w-3 h-3" />
              {stats.total_tables} Tische
            </Badge>
            <Badge variant="outline" className="flex items-center gap-1 bg-green-50 text-green-700 border-green-200">
              <Check className="w-3 h-3" />
              {stats.assigned_guests} zugewiesen
            </Badge>
            <Badge variant="outline" className="flex items-center gap-1 bg-amber-50 text-amber-700 border-amber-200">
              <UserPlus className="w-3 h-3" />
              {stats.unassigned_guests} offen
            </Badge>
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Canvas area */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Tisch-Anordnung</CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setScale(s => Math.max(0.5, s - 0.1))}>
                    <ZoomOut className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground w-12 text-center">{Math.round(scale * 100)}%</span>
                  <Button variant="outline" size="sm" onClick={() => setScale(s => Math.min(1.5, s + 0.1))}>
                    <ZoomIn className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setScale(1)}>
                    <Maximize2 className="w-4 h-4" />
                  </Button>

                  {isOwner && (
                    <Dialog open={showAddTable} onOpenChange={setShowAddTable}>
                      <DialogTrigger asChild>
                        <Button size="sm">
                          <Plus className="w-4 h-4 mr-1" />
                          Tisch
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Neuen Tisch hinzufügen</DialogTitle>
                          <DialogDescription>
                            Erstelle einen neuen Tisch für deinen Sitzplan
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div>
                            <Label>Tischname</Label>
                            <Input
                              value={newTable.name}
                              onChange={(e) => setNewTable({ ...newTable, name: e.target.value })}
                              placeholder="z.B. Tisch 1, Familie Müller..."
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>Plätze</Label>
                              <Input
                                type="number"
                                min={1}
                                max={20}
                                value={newTable.capacity}
                                onChange={(e) => setNewTable({ ...newTable, capacity: parseInt(e.target.value) || 8 })}
                              />
                            </div>
                            <div>
                              <Label>Form</Label>
                              <Select
                                value={newTable.shape}
                                onValueChange={(v) => setNewTable({ ...newTable, shape: v })}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="round">
                                    <div className="flex items-center gap-2">
                                      <Circle className="w-4 h-4" /> Rund
                                    </div>
                                  </SelectItem>
                                  <SelectItem value="rectangle">
                                    <div className="flex items-center gap-2">
                                      <RectangleHorizontal className="w-4 h-4" /> Rechteck
                                    </div>
                                  </SelectItem>
                                  <SelectItem value="square">
                                    <div className="flex items-center gap-2">
                                      <Square className="w-4 h-4" /> Quadrat
                                    </div>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div>
                            <Label>Farbe</Label>
                            <div className="flex gap-2 mt-2">
                              {['#f3f4f6', '#fef3c7', '#dcfce7', '#dbeafe', '#fce7f3', '#f3e8ff'].map(color => (
                                <button
                                  key={color}
                                  className={`w-8 h-8 rounded-full border-2 ${newTable.color === color ? 'border-primary' : 'border-transparent'}`}
                                  style={{ backgroundColor: color }}
                                  onClick={() => setNewTable({ ...newTable, color })}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setShowAddTable(false)}>Abbrechen</Button>
                          <Button onClick={handleAddTable}>Hinzufügen</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div
                ref={canvasRef}
                className="relative bg-gray-50 border rounded-lg overflow-auto"
                style={{
                  height: 500,
                  minWidth: '100%',
                  backgroundImage: 'radial-gradient(circle, #ddd 1px, transparent 1px)',
                  backgroundSize: `${20 * scale}px ${20 * scale}px`
                }}
                onClick={() => setSelectedTable(null)}
              >
                <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left', width: 1200, height: 800 }}>
                  {tables.length === 0 ? (
                    <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                      <div className="text-center">
                        <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                        <p>Noch keine Tische vorhanden</p>
                        <p className="text-sm">Füge Tische hinzu, um den Sitzplan zu erstellen</p>
                      </div>
                    </div>
                  ) : (
                    tables.map(table => (
                      <TableShape
                        key={table.id}
                        table={table}
                        isSelected={selectedTable?.id === table.id}
                        onClick={setSelectedTable}
                        guests={getGuestsAtTable(table.id)}
                        onDragStart={() => { }}
                        onDrag={handleDrag}
                        onDragEnd={handleDragEnd}
                        scale={scale}
                      />
                    ))
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Selected table details */}
          {selectedTable ? (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{selectedTable.name}</CardTitle>
                  {isOwner && (
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteTable(selectedTable.id)}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  )}
                </div>
                <CardDescription>
                  {getGuestsAtTable(selectedTable.id).length}/{selectedTable.capacity} Plätze belegt
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Edit table name */}
                {isOwner && (
                  <div>
                    <Label className="text-xs">Tischname</Label>
                    <Input
                      value={selectedTable.name}
                      onChange={(e) => {
                        const newName = e.target.value;
                        setSelectedTable({ ...selectedTable, name: newName });
                        setTables(prev => prev.map(t => t.id === selectedTable.id ? { ...t, name: newName } : t));
                      }}
                      onBlur={() => handleUpdateTable(selectedTable.id, { name: selectedTable.name })}
                      className="h-8"
                    />
                  </div>
                )}

                {/* Guests at table */}
                <div>
                  <Label className="text-xs mb-2 block">Gäste am Tisch</Label>
                  <ScrollArea className="h-32">
                    <div className="space-y-1">
                      {getGuestsAtTable(selectedTable.id).length === 0 ? (
                        <p className="text-sm text-muted-foreground">Keine Gäste zugewiesen</p>
                      ) : (
                        getGuestsAtTable(selectedTable.id).map(guest => (
                          <div key={guest.id} className="flex items-center justify-between p-2 bg-secondary/50 rounded">
                            <span className="text-sm truncate">{guest.name}</span>
                            {isOwner && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => handleRemoveAssignment(guest.id)}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </div>

                {/* Add guest to table */}
                {isOwner && getGuestsAtTable(selectedTable.id).length < selectedTable.capacity && (
                  <Dialog open={showAssignGuest} onOpenChange={setShowAssignGuest}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="w-full">
                        <UserPlus className="w-4 h-4 mr-2" />
                        Gast hinzufügen
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Gast zu &quot;{selectedTable.name}&quot; hinzufügen</DialogTitle>
                        <DialogDescription>
                          Wähle einen nicht zugewiesenen Gast aus
                        </DialogDescription>
                      </DialogHeader>
                      <ScrollArea className="max-h-64">
                        <div className="space-y-1">
                          {getUnassignedGuests().length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-4">
                              Alle Gäste sind bereits zugewiesen
                            </p>
                          ) : (
                            getUnassignedGuests().map(guest => (
                              <Button
                                key={guest.id}
                                variant="ghost"
                                className="w-full justify-start"
                                onClick={() => {
                                  handleAssignGuest(guest.id);
                                  setShowAssignGuest(false);
                                }}
                              >
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                                    {guest.name?.charAt(0).toUpperCase() || '?'}
                                  </div>
                                  <div className="text-left">
                                    <p className="text-sm font-medium">{guest.name}</p>
                                    {guest.email && <p className="text-xs text-muted-foreground">{guest.email}</p>}
                                  </div>
                                </div>
                              </Button>
                            ))
                          )}
                        </div>
                      </ScrollArea>
                    </DialogContent>
                  </Dialog>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                <Move className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Wähle einen Tisch aus,<br />um Details zu sehen</p>
              </CardContent>
            </Card>
          )}

          {/* Unassigned guests */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <UserPlus className="w-4 h-4" />
                Nicht zugewiesen
              </CardTitle>
              <CardDescription>
                {getUnassignedGuests().length} Gäste ohne Tisch
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-48">
                <div className="space-y-1">
                  {getUnassignedGuests().length === 0 ? (
                    <div className="text-center py-4">
                      <Check className="w-6 h-6 mx-auto mb-1 text-green-500" />
                      <p className="text-sm text-muted-foreground">Alle Gäste sind zugewiesen!</p>
                    </div>
                  ) : (
                    getUnassignedGuests().map(guest => (
                      <div
                        key={guest.id}
                        className="flex items-center gap-2 p-2 rounded hover:bg-secondary/50 cursor-pointer"
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData('guestId', guest.id)}
                      >
                        <div className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-medium">
                          {guest.name?.charAt(0).toUpperCase() || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{guest.name}</p>
                        </div>
                        <Grip className="w-4 h-4 text-muted-foreground" />
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
